import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Settings, ChevronDown, Palette } from "lucide-react";
import {
  logoutUser,
  selectUser,
  selectIsLoading,
} from "../../redux/slices/authSlice.js";
import { useTheme } from "../../contexts/ThemeContext.jsx";
import ThemeToggle from "../ui/ThemeToggle.jsx";
import LoadingSpinner from "../ui/LoadingSpinner.jsx";
import { useLanguage } from "../../contexts/LanguageContext.jsx";

/**
 * UserMenu Component
 *
 * Displays user information and provides logout functionality
 * for the Invoice Validation System.
 */
const UserMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);
  const { theme, effectiveTheme, THEME_OPTIONS } = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const setPosition = () => {
      if (isOpen && menuRef.current && dropdownRef.current) {
        const triggerRect = menuRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        
        dropdownRef.current.style.top = `${triggerRect.bottom + 8}px`;
        dropdownRef.current.style.left = `${triggerRect.right - dropdownRect.width}px`;
      }
    }

    setPosition();

    const handleClickOutside = (event) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };    
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      // Force navigation even if logout fails
      navigate("/login");
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = (action) => {
    setIsOpen(false);
    action();
  };

  if (!user) {
    return null;
  }

  const userDisplayName = `${user.firstname || "M"} ${user.lastname || ""}`;
  const userInitials = `${user.firstname?.charAt(0) || "M"}${
    user.lastname?.charAt(0) || "D"
  }`.toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      {/* User Menu Trigger */}
      <button
        onClick={toggleMenu}
        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-white hover:text-gray-100 hover:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* User Avatar */}
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
          {userInitials}
        </div>

        {/* User Info */}
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-white dark:text-gray-100">
            {userDisplayName}
          </div>
          <div className="text-xs text-slate-300 dark:text-gray-300 capitalize">
            {user.role.replace("_", " ")}
          </div>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`w-4 h-4 text-slate-300 dark:text-gray-300 transition-transform duration-200 ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ position: 'absolute', zIndex: 99999 }}
            className="w-56 bg-white dark:bg-slate-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div className="py-1">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                    {userInitials}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {userDisplayName}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {user.email}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-400 capitalize">
                      {user.role.replace("_", " ")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() =>
                    handleMenuItemClick(() => navigate("/settings/user-settings"))
                  }
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-900"
                >
                  <Settings className="w-4 h-4 mr-3" />
                  {t("userMenu.preferences", "Preferences")}
                </button>
              </div>

              {/* Logout Section */}
              <div className="py-1 border-t border-gray-100 dark:border-slate-700">
                <button
                  onClick={() => handleMenuItemClick(handleLogout)}
                  disabled={isLoading}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-700 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" className="mr-3" text="" />
                  ) : (
                    <LogOut className="w-4 h-4 mr-3" />
                  )}
                  {isLoading
                    ? t("userMenu.signingOut", "Signing out...")
                    : t("userMenu.signOut", "Sign Out")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default UserMenu;
